import { useState } from 'react';
import { addDays, format, isSameDay, parse, parseISO, startOfDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import { DragScroll } from './DragScroll';
import { TimeWheel } from './TimeWheel';
import { CalendarSingleSelect, DATE_KEY_FORMAT } from './CalendarSingleSelect';

interface DateTimeFieldProps {
  label: string;
  /** datetime-local 형식 (yyyy-MM-dd'T'HH:mm). 빈 문자열 = 미선택 */
  value: string;
  onChange: (value: string) => void;
  /** 기준 날짜(ISO). 지정 시 당일·다음날(자정 넘김)만 선택 가능 — 확정 날짜가 있는 일정용 */
  anchorDate?: string | undefined;
}

const VALUE_FORMAT = "yyyy-MM-dd'T'HH:mm";
const DEFAULT_HOUR = 19;

function parseValue(value: string): Date | null {
  if (!value) return null;
  const d = parse(value, VALUE_FORMAT, new Date());
  return Number.isNaN(d.getTime()) ? null : d;
}

// 모바일 날짜·시간 선택 — 날짜는 달력(anchorDate 있으면 칩 2개), 시간은 세로 휠.
// 달력 범위 밖이거나 정밀 입력은 '직접 입력'으로 네이티브 datetime-local 폴백.
export function DateTimeField({ label, value, onChange, anchorDate }: DateTimeFieldProps) {
  // anchorDate(확정 날짜)가 없으면 달력 없이 직접입력 모드로 시작 — 달력이 길어 모바일에서 불편
  const [manual, setManual] = useState(!anchorDate);
  const today = startOfDay(new Date());
  const selected = parseValue(value);
  const anchor = anchorDate ? startOfDay(new Date(anchorDate)) : null;

  function pickDate(date: Date) {
    const next = new Date(date);
    next.setHours(selected?.getHours() ?? DEFAULT_HOUR, selected?.getMinutes() ?? 0, 0, 0);
    onChange(format(next, VALUE_FORMAT));
  }

  function pickTime(hour: number, minute: number) {
    const base = selected ?? new Date(today);
    const next = new Date(base);
    next.setHours(hour, minute, 0, 0);
    onChange(format(next, VALUE_FORMAT));
  }

  const chipBase =
    'pressable shrink-0 rounded-xl text-small font-semibold focus-visible:outline-none focus-visible:shadow-focus-ring';
  const chipOff = 'bg-surface-muted text-ink-muted hover:bg-surface-border/60';
  const chipOn = 'bg-ink text-canvas';

  const selectedDateKey = selected ? format(selected, DATE_KEY_FORMAT) : null;

  function onCalendarChange(dateKey: string) {
    pickDate(parseISO(dateKey));
  }

  if (manual) {
    return (
      <div>
        <div className="flex items-center justify-between">
          <span className="text-small font-semibold text-ink-muted">{label}</span>
          <button
            type="button"
            onClick={() => setManual(false)}
            className="text-small font-semibold text-ink-faint underline-offset-2 hover:underline focus-visible:outline-none focus-visible:underline"
          >
            간편 선택
          </button>
        </div>
        <input
          type="datetime-local"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={anchor ? format(anchor, "yyyy-MM-dd'T'00:00") : undefined}
          max={anchor ? format(addDays(anchor, 1), "yyyy-MM-dd'T'23:59") : undefined}
          aria-label={label}
          className="mt-1.5 w-full rounded-xl border border-surface-border bg-surface px-3.5 py-2.5 text-ui text-ink focus-visible:outline-none focus-visible:shadow-focus-ring"
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-small font-semibold text-ink-muted">{label}</span>
        <button
          type="button"
          onClick={() => setManual(true)}
          className="text-small font-semibold text-ink-faint underline-offset-2 hover:underline focus-visible:outline-none focus-visible:underline"
        >
          직접 입력
        </button>
      </div>

      {/* 날짜 선택 — anchorDate 있으면 칩 2개, 없으면 달력 */}
      {anchor ? (
        <DragScroll
          role="group"
          aria-label={`${label} — 날짜`}
          data-vaul-no-drag
          className="scrollbar-none -mx-1 mt-2 flex gap-1.5 overflow-x-auto px-1 pb-1"
        >
          {[anchor, addDays(anchor, 1)].map((d, i) => {
            const on = selected ? isSameDay(d, selected) : false;
            return (
              <button
                key={d.toISOString()}
                type="button"
                aria-pressed={on}
                onClick={() => pickDate(d)}
                className={`${chipBase} flex w-[52px] flex-col items-center gap-0.5 py-2 ${on ? chipOn : chipOff}`}
              >
                <span className={`text-[11px] font-medium ${on ? 'text-canvas/75' : 'text-ink-faint'}`}>
                  {i === 0 ? '당일' : '다음날'}
                </span>
                <span className="text-ui font-semibold">{format(d, 'd')}</span>
              </button>
            );
          })}
        </DragScroll>
      ) : (
        <div className="mt-2">
          <CalendarSingleSelect
            selected={selectedDateKey}
            onChange={onCalendarChange}
            minDate={today}
          />
        </div>
      )}

      {/* 시간 — 선택된 날짜가 있을 때만 (세로 휠, 1분 단위) */}
      {selected ? (
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-small font-semibold text-ink-muted">시간</span>
            <span className="text-small font-semibold text-ink tabular-nums">
              {format(selected, 'M월 d일 (EEE) HH:mm', { locale: ko })}
            </span>
          </div>
          <TimeWheel
            hour={selected.getHours()}
            minute={selected.getMinutes()}
            onChange={pickTime}
            allowManual={false}
          />
        </div>
      ) : (
        <p className="mt-2 text-small text-ink-faint">날짜를 먼저 선택해주세요.</p>
      )}
    </div>
  );
}
